import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import {
  US_STATES,
  clearLeadId,
  readLeadId,
} from "@/components/forms/quick-quote-shared";
import {
  completeQuickQuote,
  deliverQuickQuoteWithoutPhoto,
  uploadQuickQuotePhotos,
} from "@/lib/forms/quick-quote.functions";
import { getTrackingParams } from "@/lib/utm-tracking";
import stepIcon from "@/assets/restore/icon-step-1.webp";
import bookletImg from "@/assets/restore/BOOKLET.webp";

const TITLE = "Finish Your Quick Quote - RESTORE Hair";
const DESCRIPTION =
  "Add your address to finish your RESTORE Hair quick quote and get matched with your nearest hair restoration clinic.";
const CANONICAL_URL = "https://restorehair.com/quick-quote-page-2/";

export const Route = createFileRoute("/quick-quote-page-2")({
  validateSearch: (search: Record<string, unknown>) => ({
    qq: typeof search["qq"] === "string" ? (search["qq"] as string) : undefined,
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: CANONICAL_URL }],
  }),
  component: QuickQuotePage2,
});

/** Boxed field with the label sitting inside the top of the control. */
function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="border border-border bg-background px-4 pb-2 pt-2.5">
      <label
        htmlFor={id}
        className="block text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const controlCls =
  "w-full border-0 bg-transparent p-0 pt-1.5 pb-1 text-base text-foreground outline-none focus:ring-0";

const ACUITY_OWNER = "24213130";
const ACUITY_APPOINTMENTS = [
  { label: "Charlotte, NC", type: "49645045" },
  { label: "Denver, CO", type: "49646910" },
  { label: "Boulder, CO", type: "49646889" },
  { label: "Greenwood Village, CO", type: "49646857" },
  { label: "Manhattan, NY", type: "49646097" },
  { label: "Oak Brook, IL", type: "49647719" },
  { label: "Plano, TX", type: "49647474" },
  { label: "Virtual Consultation", type: "26951951" },
];

function AcuityButton({ label, type }: { label: string; type: string }) {
  const href = `https://app.acuityscheduling.com/schedule.php?owner=${ACUITY_OWNER}&appointmentType=${type}`;
  return (
    <a
      href={href}
      className="block border border-border bg-background px-4 py-3 text-center text-sm font-semibold text-foreground transition hover:bg-muted"
    >
      {label}
    </a>
  );
}

/**
 * Phones (especially iOS) often report an empty or non-image `File.type` for
 * camera photos and HEIC files, so fall back to the file extension.
 */
const IMAGE_EXTENSIONS = [
  "jpg", "jpeg", "png", "heic", "heif", "webp", "gif", "bmp", "tif", "tiff", "avif",
];

function extensionOf(file: File): string {
  const match = /\.([a-z0-9]+)$/iu.exec(file.name.trim());
  return match ? match[1]!.toLowerCase() : "";
}

function looksLikeImage(file: File): boolean {
  if (file.type.startsWith("image/")) return true;
  return IMAGE_EXTENSIONS.includes(extensionOf(file));
}

/** Best-effort image MIME type when the browser does not supply one. */
function mimeForFile(file: File): string {
  if (file.type.startsWith("image/")) return file.type;
  const ext = extensionOf(file);
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "tif") return "image/tiff";
  return ext ? `image/${ext}` : "image/jpeg";
}

function readFileAsBase64(file: File): Promise<{ base64: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const [header, base64] = result.split(",");
      const headerMime = header?.match(/data:(.+);base64/)?.[1];
      const mime =
        headerMime && headerMime.startsWith("image/") ? headerMime : mimeForFile(file);
      if (!base64) {
        reject(new Error("We couldn't read that photo. Please try another one."));
        return;
      }
      resolve({ base64, mime });
    };
    reader.onerror = () =>
      reject(new Error("We couldn't read that photo. Please try another one."));
    reader.readAsDataURL(file);
  });
}

/**
 * Fire a Zaraz funnel-step event (no personal data — step name and photo
 * count only). Best-effort: tracking must never block the quote flow.
 */
function trackZaraz(event: string, props?: Record<string, number>) {
  try {
    const zaraz = (window as unknown as { zaraz?: { track?: (n: string, p?: unknown) => void } }).zaraz;
    zaraz?.track?.(event, props);
  } catch {
    /* ignore */
  }
}

/**
 * Phone photos are 3-8 MB each; base64-encoding five of them into one request
 * makes a ~40 MB upload that mobile networks drop ("Load failed"). Downscale
 * each image to at most 1600px / JPEG before sending. Falls back to the
 * original file when the browser cannot decode it (e.g. HEIC outside Safari).
 */
async function downscaleImage(file: File): Promise<{ base64: string; mime: string }> {
  const MAX_EDGE = 1600;
  try {
    if (typeof createImageBitmap !== "function") throw new Error("no bitmap support");
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas context");
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
    const base64 = dataUrl.split(",")[1];
    if (!base64) throw new Error("encode failed");
    return { base64, mime: "image/jpeg" };
  } catch {
    return readFileAsBase64(file);
  }
}


function QuickQuotePage2() {
  const navigate = useNavigate();
  const complete = useServerFn(completeQuickQuote);
  const uploadPhotos = useServerFn(uploadQuickQuotePhotos);
  const skipPhoto = useServerFn(deliverQuickQuoteWithoutPhoto);

  const MAX_PHOTOS = 6;

  const [leadId, setLeadId] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [step, setStep] = useState<"address" | "photo" | "done">("address");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [photoCount, setPhotoCount] = useState(0);
  const [progress, setProgress] = useState<string | null>(null);

  const [addr, setAddr] = useState({
    address1: "",
    address2: "",
    city: "",
    state: "",
    address_zip: "",
  });
  const setAddrField = (key: keyof typeof addr) => (value: string) =>
    setAddr((prev) => ({ ...prev, [key]: value }));

  // Some third-party scripts (tag manager / form trackers / autofill helpers)
  // blank out address inputs after we render. Re-assert React's value onto the
  // DOM node on every render so anything that wipes a field is undone.
  const addrRefs = useRef<Record<string, HTMLInputElement | HTMLSelectElement | null>>({});
  useEffect(() => {
    for (const [key, value] of Object.entries(addr)) {
      const el = addrRefs.current[key];
      if (el && el.value !== value) el.value = value;
    }
  });

  useEffect(() => {
    setLeadId(readLeadId());
    setResolved(true);
  }, []);

  async function onAddressSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!leadId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const cityValue = addr.city.trim();
      const stateValue = addr.state.trim();
      const addressZip = addr.address_zip.trim();
      await complete({
        data: {
          lead_id: leadId,
          address1: addr.address1.trim(),
          address2: addr.address2.trim() || undefined,
          city: cityValue,
          state: stateValue,
          address_zip: addressZip,
          tracking: getTrackingParams(),
        },
      });
      trackZaraz("qq_address_saved");
      setStep("photo");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onPhotoSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!leadId || submitting) return;
    if (selectedFiles.length === 0) {
      setError("Please select at least one photo or skip this step.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Upload one photo per request. A single request carrying every photo is
      // too large for flaky mobile connections and fails as "Load failed".
      let uploaded = 0;
      for (let i = 0; i < selectedFiles.length; i += 1) {
        const file = selectedFiles[i]!;
        const { base64, mime } = await downscaleImage(file);
        setProgress(`Uploading photo ${i + 1} of ${selectedFiles.length}…`);
        const res = await uploadPhotos({
          data: {
            lead_id: leadId,
            photos: [{ photo_base64: base64, photo_mime: mime }],
            tracking: getTrackingParams(),
            // Only the final photo sends the lead on, so multiple photos
            // still create a single record.
            deliver: i === selectedFiles.length - 1,
          },
        });
        uploaded += res.photo_urls.length;
      }
      setPhotoCount(uploaded);
      trackZaraz("qq_photo_uploaded", { photos: uploaded });
      clearLeadId();
      navigate({ to: "/contact-us/confirmation-2s" });
    } catch (err) {
      // Technical upload failures (storage errors) are logged, never shown raw.
      console.error("[quickQuote] photo upload failed", err);
      const raw = err instanceof Error ? err.message : "";
      const friendly =
        raw && !/failed \[|Google|fetch|network error|load failed/i.test(raw)
          ? raw
          : "We couldn't upload those photos. Please try again with fewer photos, or choose \u201CSkip Photo & Finish\u201D and we'll collect them on your call.";
      setError(friendly);
    } finally {
      setProgress(null);
      setSubmitting(false);
    }
  }


  async function onSkipPhoto() {
    if (!leadId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await skipPhoto({ data: { lead_id: leadId, tracking: getTrackingParams() } });
      trackZaraz("qq_photo_skipped");
      clearLeadId();
      navigate({ to: "/contact-us/confirmation-2s" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function addFiles(files: File[]) {
    setError(null);
    const accepted: File[] = [];
    for (const file of files) {
      if (!looksLikeImage(file)) {
        setError("Please choose image files only (JPG, PNG, HEIC, etc.).");
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        setError("Each image must be smaller than 20 MB.");
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setSelectedFiles((prev) => {
      const room = MAX_PHOTOS - prev.length;
      if (room <= 0) {
        setError(`You can upload up to ${MAX_PHOTOS} photos.`);
        return prev;
      }
      const added = accepted.slice(0, room);
      if (accepted.length > room) {
        setError(`You can upload up to ${MAX_PHOTOS} photos — the extra files were not added.`);
      }
      setPreviewUrls((prevUrls) => [
        ...prevUrls,
        ...added.map((file) => URL.createObjectURL(file)),
      ]);
      return [...prev, ...added];
    });
  }

  function onFilesChange(list: FileList | null) {
    if (!list || list.length === 0) return;
    addFiles(Array.from(list));
  }

  function removeFile(index: number) {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10 lg:px-8 md:py-16">
        {resolved && !leadId ? (
          <div className="mx-auto max-w-3xl border border-border bg-background p-6 md:p-8">
            <h2 className="text-xl font-extrabold text-foreground">
              Let&apos;s start with your details
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We couldn&apos;t find your quote in progress. Please complete the first step.
            </p>
            <a
              href="/quick-quote-part-1/"
              className="mt-5 inline-block bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90"
            >
              Start my quick quote
            </a>
          </div>
        ) : (
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left — confirmation column */}
            <div>
              <div className="flex items-center gap-4">
                <img
                  src={stepIcon}
                  alt=""
                  width={64}
                  height={64}
                  loading="eager"
                  className="h-16 w-16 object-contain"
                />
                <h1 className="text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
                  Almost <span className="font-extrabold">There!</span>
                </h1>
              </div>
              <p className="mt-6 max-w-md text-base leading-relaxed text-foreground">
                Thanks for requesting a quick quote. Someone on our staff will call you very
                shortly to help deliver pricing information and to answer any additional
                questions.
              </p>

              <img
                src={bookletImg}
                alt="RESTORE VIP information package"
                width={420}
                height={420}
                loading="lazy"
                className="mt-10 max-w-full object-contain"
              />
            </div>

            {/* Right — step content */}
            <div>
              {step === "address" && (
                <>
                  <div className="flex gap-3" aria-label="Step 2 of 3">
                    <span className="h-1 flex-1 bg-foreground" />
                    <span className="h-1 flex-1 bg-foreground" />
                    <span className="h-1 flex-1 bg-border" />
                  </div>
                  <h2 className="mt-8 text-2xl font-light text-foreground md:text-3xl">
                    Where can we send your VIP Package
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Enter your address below and we&apos;ll send you more information on our hair
                    restoration processes and special offers reserved for VIPs.
                  </p>

                  <form onSubmit={onAddressSubmit} className="mt-6 space-y-4">
                    <Field id="qq_address1" label="Address 1 *">
                      <input
                        className={controlCls}
                        id="qq_address1"
                        name="qq_address1"
                        ref={(el) => { addrRefs.current.address1 = el; }}
                        required
                        maxLength={255}
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        value={addr.address1}
                        onChange={(e) => setAddrField("address1")(e.target.value)}
                      />
                    </Field>
                    <Field id="qq_address2" label="Address 2">
                      <input
                        className={controlCls}
                        id="qq_address2"
                        name="qq_address2"
                        ref={(el) => { addrRefs.current.address2 = el; }}
                        maxLength={255}
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        value={addr.address2}
                        onChange={(e) => setAddrField("address2")(e.target.value)}
                      />
                    </Field>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field id="qq_city" label="City *">
                        <input
                          className={controlCls}
                          id="qq_city"
                          name="qq_city"
                          ref={(el) => { addrRefs.current.city = el; }}
                          required
                          maxLength={80}
                          autoComplete="off"
                          data-lpignore="true"
                          data-1p-ignore="true"
                          value={addr.city}
                          onChange={(e) => setAddrField("city")(e.target.value)}
                        />
                      </Field>
                      <Field id="qq_state" label="State *">
                        <select
                          className={controlCls}
                          id="qq_state"
                          name="qq_state"
                          ref={(el) => { addrRefs.current.state = el; }}
                          required
                          autoComplete="off"
                          value={addr.state}
                          onChange={(e) => setAddrField("state")(e.target.value)}
                        >
                          <option value="" disabled>
                            Select a state
                          </option>
                          {US_STATES.map((state: string) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <Field id="qq_address_zip" label="Zip Code *">
                      <input
                        className={controlCls}
                        id="qq_address_zip"
                        name="qq_address_zip"
                        ref={(el) => { addrRefs.current.address_zip = el; }}
                        required
                        maxLength={20}
                        autoComplete="off"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        value={addr.address_zip}
                        onChange={(e) => setAddrField("address_zip")(e.target.value)}
                      />
                    </Field>

                    {/* Honeypot */}
                    <div className="sr-only" aria-hidden="true">
                      <label htmlFor="website">If you are human, leave this field blank.</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {error && <p className="text-sm text-destructive">{error}</p>}

                    <button
                      type="submit"
                      disabled={submitting || !resolved}
                      className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? "Sending…" : "Next Step"}
                    </button>
                  </form>
                </>
              )}

              {step === "photo" && (
                <>
                  <div className="flex gap-3" aria-label="Step 3 of 3">
                    <span className="h-1 flex-1 bg-foreground" />
                    <span className="h-1 flex-1 bg-foreground" />
                    <span className="h-1 flex-1 bg-foreground" />
                  </div>
                  <h2 className="mt-8 text-2xl font-light text-foreground md:text-3xl">
                    Last Step!
                  </h2>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Upload a few photos of your hair from different angles. This helps our
                    specialists prepare a more accurate quote before we call.
                  </p>

                  <form onSubmit={onPhotoSubmit} className="mt-6 space-y-5">
                    <label
                      htmlFor="photo"
                      className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 px-6 py-10 text-center transition hover:bg-muted/50"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        onFilesChange(e.dataTransfer.files);
                      }}
                    >
                      <span className="text-4xl" aria-hidden="true">
                        📷
                      </span>
                      <span className="mt-3 text-sm font-semibold text-foreground">
                        Drag &amp; drop your photos here
                      </span>
                      <span className="mt-1 text-xs text-muted-foreground">
                        or click to browse — up to {MAX_PHOTOS} photos (JPG, PNG, HEIC up to
                        20 MB each)
                      </span>
                      <input
                        id="photo"
                        name="photo"
                        type="file"
                        multiple
                        accept="image/*,.heic,.heif,.jpg,.jpeg,.png,.webp"
                        className="sr-only"
                        onChange={(e) => {
                          onFilesChange(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>

                    {previewUrls.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {previewUrls.length} of {MAX_PHOTOS} photos selected
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                          {previewUrls.map((url, index) => (
                            <div key={url} className="relative">
                              <img
                                src={url}
                                alt={`Selected photo ${index + 1}`}
                                className="h-24 w-full rounded border border-border object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                aria-label={`Remove photo ${index + 1}`}
                                className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background transition hover:bg-foreground/80"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {error && <p className="text-sm text-destructive">{error}</p>}

                    <button
                      type="submit"
                      disabled={submitting || selectedFiles.length === 0}
                      className="w-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-wider text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting
                        ? (progress ?? "Uploading…")
                        : selectedFiles.length > 1
                          ? `Upload ${selectedFiles.length} Photos & Finish`
                          : "Upload Photo & Finish"}
                    </button>
                    <button
                      type="button"
                      onClick={onSkipPhoto}
                      disabled={submitting}
                      className="w-full border border-border bg-background px-6 py-4 text-sm font-bold uppercase tracking-wider text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Skip Photo & Finish
                    </button>
                  </form>
                </>
              )}

              {step === "done" && (
                <div className="border border-border bg-background p-6 md:p-8">
                  <h2 className="text-2xl font-light text-foreground md:text-3xl">
                    Thank You!
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-foreground">
                    Your quick quote has been submitted. A RESTORE® specialist will reach out
                    shortly with your personalized quote.
                  </p>
                  {photoCount > 0 && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      {photoCount === 1
                        ? "Your photo was uploaded successfully."
                        : `Your ${photoCount} photos were uploaded successfully.`}
                    </p>
                  )}

                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-foreground">
                      Schedule your complimentary consultation
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Select a convenient location to book your appointment:
                    </p>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {ACUITY_APPOINTMENTS.map((appt) => (
                        <AcuityButton key={appt.type} label={appt.label} type={appt.type} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
